/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ClientResponse } from '../models/client-response';
import { CreateClientRequest } from '../models/create-client-request';
import { GenericResponse } from '../models/generic-response';
import { GetClientsResponse } from '../models/get-clients-response';
import { PasswordResetRequest } from '../models/password-reset-request';
import { UpdateClientRequest } from '../models/update-client-request';

/**
 * client management apis
 */
@Injectable({ providedIn: 'root' })
export class ClientsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllClients()` */
  static readonly GetAllClientsPath = '/api/v1/clients';

  /**
   * Get list of clients.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllClients()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllClients$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name - userName - mobileNumber
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetClientsResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientsService.GetAllClientsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetClientsResponse>;
        })
      );
  }

  /**
   * Get list of clients.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllClients$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllClients(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name - userName - mobileNumber
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetClientsResponse> {
    return this.getAllClients$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetClientsResponse>): GetClientsResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createClient()` */
  static readonly CreateClientPath = '/api/v1/clients';

  /**
   * Create a new client.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClient()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClient$Response(
    params: {
      body: CreateClientRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClientResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientsService.CreateClientPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClientResponse>;
        })
      );
  }

  /**
   * Create a new client.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createClient$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClient(
    params: {
      body: CreateClientRequest;
    },
    context?: HttpContext
  ): Observable<ClientResponse> {
    return this.createClient$Response(params, context).pipe(
      map((r: StrictHttpResponse<ClientResponse>): ClientResponse => r.body)
    );
  }

  /** Path part for operation `getClientById()` */
  static readonly GetClientByIdPath = '/api/v1/clients/{id}';

  /**
   * Get client details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientById$Response(
    params: {
      /**
       * The client's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClientResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientsService.GetClientByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClientResponse>;
        })
      );
  }

  /**
   * Get client details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getClientById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientById(
    params: {
      /**
       * The client's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<ClientResponse> {
    return this.getClientById$Response(params, context).pipe(
      map((r: StrictHttpResponse<ClientResponse>): ClientResponse => r.body)
    );
  }

  /** Path part for operation `softDeleteClient()` */
  static readonly SoftDeleteClientPath = '/api/v1/clients/{id}';

  /**
   * Soft delete client.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `softDeleteClient()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteClient$Response(
    params: {
      /**
       * The client's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientsService.SoftDeleteClientPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Soft delete client.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `softDeleteClient$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteClient(
    params: {
      /**
       * The client's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.softDeleteClient$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateClient()` */
  static readonly UpdateClientPath = '/api/v1/clients/{id}';

  /**
   * Update client by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClient()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClient$Response(
    params: {
      /**
       * The client's id that needs to be updated
       */
      id: number;
      body: UpdateClientRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClientResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientsService.UpdateClientPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClientResponse>;
        })
      );
  }

  /**
   * Update client by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateClient$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClient(
    params: {
      /**
       * The client's id that needs to be updated
       */
      id: number;
      body: UpdateClientRequest;
    },
    context?: HttpContext
  ): Observable<ClientResponse> {
    return this.updateClient$Response(params, context).pipe(
      map((r: StrictHttpResponse<ClientResponse>): ClientResponse => r.body)
    );
  }

  /** Path part for operation `resetPassword()` */
  static readonly ResetPasswordPath = '/api/v1/clients/reset-password';

  /**
   * Reset user password.
   *
   * Allows a user to reset their password.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `resetPassword()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  resetPassword$Response(
    params: {
      body: PasswordResetRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientsService.ResetPasswordPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Reset user password.
   *
   * Allows a user to reset their password.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `resetPassword$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  resetPassword(
    params: {
      body: PasswordResetRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.resetPassword$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }
}
